import React, { useMemo } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { AppErrorBoundary } from "./AppErrorBoundary";
import CodeHighlighter from "./code/CodeHighlighter";
import {
  useTheme as useThemeContext,
  type Theme,
} from "./context/theme-context";
import DownloadTableButton, {
  type DownloadableTableData,
  type DownloadTableFormat,
} from "./DownloadTable";
import { useTableExtractor } from "./hooks/use-table-extractor";
import { m } from "./paraglide/messages";
import {
  PlainTable,
  PlainTableCell,
  PlainTableHeader,
  PlainTableHeaderCell,
  PlainTableRow,
} from "./PlainTable";

type MarkdownPreviewType = "audit" | "help" | "chat";

const markdownPreview = tv({
  slots: {
    container: "max-w-none text-text-primary",
    img: "my-4",
    tableWrapper: "my-4 overflow-x-auto",
    p: "mb-3 leading-7 text-text-primary",
    ul: "ml-6 list-disc text-text-primary",
    ol: "ml-6 list-decimal text-text-primary",
    h1: "",
    h2: "",
    h3: "",
  },
  variants: {
    type: {
      audit: {
        container: "px-6 py-4",
        p: "text-sm",
        ul: "mb-3 space-y-2 text-sm",
        ol: "mb-3 space-y-2 text-sm",
        h1: "mt-4 mb-2 text-2xl font-bold text-text-primary",
        h2: "mt-4 mb-2 text-xl font-bold text-text-primary",
        h3: "mt-4 mb-2 text-lg font-bold text-text-primary",
      },
      help: {
        container: "px-6 py-4",
        img: "max-w-full rounded-lg",
        p: "text-base",
        ul: "mb-3 space-y-2 text-base",
        ol: "mb-3 space-y-2 text-base",
        h1: "mt-6 mb-2 text-2xl font-bold text-text-primary",
        h2: "mt-6 mb-2 text-xl font-bold text-text-primary",
        h3: "mt-6 mb-2 text-lg font-bold text-text-primary",
      },
      chat: {
        img: "max-h-96",
        p: "text-sm",
        ul: "my-2 space-y-1",
        ol: "my-2 space-y-1",
        h1: "text-xl font-medium",
        h2: "text-lg font-medium",
        h3: "font-medium",
      },
    },
  },
});

const SYNTAX_HIGHLIGHT_TYPES = new Set<MarkdownPreviewType>([
  "audit",
  "help",
  "chat",
]);

interface MarkdownPreviewProps {
  readonly content: string;
  readonly type?: MarkdownPreviewType;
  readonly className?: string;
  readonly enableTableExport?: boolean;
  readonly theme?: Theme;
  readonly onExport?: (
    format: DownloadTableFormat,
    data: DownloadableTableData,
  ) => void;
}

function ChatTableWrapper({
  children,
  enableExport,
  onExport,
}: {
  readonly children: React.ReactNode;
  readonly enableExport: boolean;
  readonly onExport?: (
    format: DownloadTableFormat,
    data: DownloadableTableData,
  ) => void;
}) {
  const tableRef = React.useRef<HTMLTableElement>(null);
  const extractedData = useTableExtractor({ tableRef, enabled: enableExport });

  const table = useMemo(
    () => (
      <PlainTable ref={tableRef} className="table-auto rtl:text-right">
        {children}
      </PlainTable>
    ),
    [children],
  );

  return (
    <div className="my-2 overflow-x-auto">
      {extractedData && onExport && (
        <div className="flex justify-end gap-2">
          <DownloadTableButton
            extractedData={extractedData}
            onExport={onExport}
          />
        </div>
      )}
      {table}
    </div>
  );
}

function buildMarkdownComponents({
  type,
  theme,
  enableTableExport,
  onExport,
}: {
  type: MarkdownPreviewType;
  theme: Theme;
  enableTableExport: boolean;
  onExport?: (format: DownloadTableFormat, data: DownloadableTableData) => void;
}): Components {
  const slots = markdownPreview({ type });
  const isChatMode = type === "chat";
  const enableSyntaxHighlight = SYNTAX_HIGHLIGHT_TYPES.has(type);

  return {
    code({ className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : "sql";
      const codeString = React.Children.toArray(children)
        .filter(
          (child) => typeof child === "string" || typeof child === "number",
        )
        .join("")
        .replace(/\n$/, "");
      const isInline = !className;

      if (isInline) {
        return (
          <code
            className="bg-background-2 rounded-lg px-1.5 py-0.5 text-xs"
            {...props}
          >
            {children}
          </code>
        );
      }

      if (
        enableSyntaxHighlight &&
        (language === "sql" || language === "python")
      ) {
        return (
          <div className="border-border my-3 overflow-hidden rounded-lg border">
            <CodeHighlighter language={language} theme={theme}>
              {codeString}
            </CodeHighlighter>
          </div>
        );
      }

      return (
        <div className="bg-background-2 my-3 overflow-x-auto rounded-lg p-3">
          <code className="text-text-secondary text-sm">{codeString}</code>
        </div>
      );
    },
    pre({ children }) {
      return <>{children}</>;
    },
    h1: ({ children, node: _node, className, ...props }) => (
      <h1 className={twMerge(slots.h1(), className)} {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, node: _node, className, ...props }) => (
      <h2 className={twMerge(slots.h2(), className)} {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, node: _node, className, ...props }) => (
      <h3 className={twMerge(slots.h3(), className)} {...props}>
        {children}
      </h3>
    ),
    p: ({ children, node: _node, className, ...props }) => (
      <p className={twMerge(slots.p(), className)} {...props}>
        {children}
      </p>
    ),
    ul: ({ children, node: _node, className, ...props }) => (
      <ul className={twMerge(slots.ul(), className)} {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, node: _node, className, ...props }) => (
      <ol className={twMerge(slots.ol(), className)} {...props}>
        {children}
      </ol>
    ),
    li: ({ children, node: _node, className, ...props }) => (
      <li className={twMerge("text-text-primary", className)} {...props}>
        {children}
      </li>
    ),
    strong: ({ children, node: _node, ...props }) => (
      <strong className="text-text-primary font-semibold" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, node: _node, ...props }) => (
      <em className="text-text-primary italic" {...props}>
        {children}
      </em>
    ),
    blockquote: ({ children, node: _node, ...props }) => (
      <blockquote
        className="border-border text-text-secondary my-4 border-l-4 pl-4 italic"
        {...props}
      >
        {children}
      </blockquote>
    ),
    a: ({ children, href, node: _node, className, ...props }) => (
      <a
        href={href}
        className={twMerge(
          "text-primary underline hover:opacity-80",
          className,
        )}
        {...props}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    img: ({ src, alt, node: _node, className, ...props }) => (
      <img
        src={src}
        alt={alt}
        {...props}
        className={twMerge(slots.img(), className)}
      />
    ),
    hr: ({ ...props }) => (
      <hr {...props} className={isChatMode ? "my-4" : undefined} />
    ),
    table: ({ children }) => {
      if (isChatMode) {
        return (
          <ChatTableWrapper
            enableExport={enableTableExport}
            onExport={onExport}
          >
            {children}
          </ChatTableWrapper>
        );
      }

      return (
        <div className={slots.tableWrapper()}>
          <PlainTable
            color="document"
            size={type === "help" ? "base" : "sm"}
            roundedBottom={false}
            className="table-auto"
          >
            {children}
          </PlainTable>
        </div>
      );
    },
    thead: ({ children }) => <PlainTableHeader>{children}</PlainTableHeader>,
    tr: ({ children }) => <PlainTableRow>{children}</PlainTableRow>,
    th: ({ children, style }) => (
      <PlainTableHeaderCell style={style}>{children}</PlainTableHeaderCell>
    ),
    td: ({ children, style }) => (
      <PlainTableCell style={style}>{children}</PlainTableCell>
    ),
  };
}

export function MarkdownPreview({
  content,
  type = "audit",
  className,
  enableTableExport = true,
  theme: themeProp,
  onExport,
}: MarkdownPreviewProps) {
  const slots = markdownPreview({ type });
  const contextTheme = useThemeContext();
  const theme = themeProp ?? contextTheme;

  const components = useMemo(
    () => buildMarkdownComponents({ type, theme, enableTableExport, onExport }),
    [type, theme, enableTableExport, onExport],
  );

  return (
    <div className={twMerge(slots.container(), className)}>
      <AppErrorBoundary
        area="markdown-preview"
        description={m.markdown_render_error_message()}
        resetKeys={[content, type, enableTableExport]}
      >
        <Markdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </Markdown>
      </AppErrorBoundary>
    </div>
  );
}
